/**
 * API client for the Meta read endpoints — `/meta/campaigns/...` plus
 * `/meta/ad-sets/{id}/ads`
 * (A3 §8.1 read subset). Mirrors `campaignsApi` (./campaigns.ts) shape
 * but is read-only — A4 will add the write methods alongside.
 *
 * Backend contract: `src/api/routers/meta_campaigns.py`. Every endpoint
 * is gated behind `require_meta_linkage_access` server-side; this client
 * just calls them and surfaces 403s like any other error.
 *
 * The `platform_source` discriminator is stamped onto each returned
 * `MetaCampaign` here so the Account page can mix Meta + Google entries
 * in one grid without a separate type predicate at the render site.
 */
import apiClient from './client';
import {
  META_PLATFORM_SOURCE,
  type MetaAdList,
  type MetaAdSetList,
  type MetaCampaign,
  type MetaCampaignDetail,
} from '@/types/metaCampaign';
import { debug } from '@/utils/debug';

// ── Wire-contract types ─────────────────────────────────────────────────
// The backend (`src/api/routers/meta_campaigns.py` →
// `src/api/services/meta_campaigns_service.py`) projects the saved
// `campaign_list` entries through Pydantic models that do NOT include
// `platform_source` — the discriminator is stamped on each entry
// client-side here, in `tagPlatform`, so the rest of the UI can switch
// on a guaranteed-present field.
//
// These `*Api` types reflect what the wire actually carries; the
// augmented `MetaCampaign` / `MetaCampaignDetail` (which include
// `platform_source`) are what consumers see after `tagPlatform` runs.
// Keeping the two split prevents accidental use of un-tagged server
// data elsewhere.
type MetaCampaignApi = Omit<MetaCampaign, 'platform_source'>;
type MetaCampaignDetailApi = Omit<MetaCampaignDetail, 'platform_source'>;
interface MetaCampaignListApi {
  campaigns: MetaCampaignApi[];
  total: number;
}

/** Attach the `platform_source` discriminator (server uses the same string). */
function tagPlatform(c: MetaCampaignApi): MetaCampaign {
  return { ...c, platform_source: META_PLATFORM_SOURCE };
}

export const metaCampaignsApi = {
  async getCampaigns(campaignStatus?: string): Promise<MetaCampaign[]> {
    debug.log('metaCampaignsApi: Fetching Meta campaigns');
    const response = await apiClient.get<MetaCampaignListApi>('/meta/campaigns', {
      // Param name `campaign_status` mirrors the backend's Query alias
      // (Theme #3) — `meta_campaigns.py:list_meta_campaigns_endpoint`
      // renames it from `status` to avoid shadowing `fastapi.status`.
      params: campaignStatus ? { campaign_status: campaignStatus } : undefined,
    });
    const campaigns = response.data.campaigns ?? [];
    debug.log('metaCampaignsApi: Got Meta campaigns', { count: campaigns.length });
    return campaigns.map(tagPlatform);
  },

  async getCampaign(campaignId: string): Promise<MetaCampaignDetail> {
    debug.log('metaCampaignsApi: Fetching Meta campaign', { campaignId });
    const response = await apiClient.get<MetaCampaignDetailApi>(
      `/meta/campaigns/${encodeURIComponent(campaignId)}`,
    );
    return { ...response.data, platform_source: META_PLATFORM_SOURCE };
  },

  async getAdSets(campaignId: string): Promise<MetaAdSetList> {
    debug.log('metaCampaignsApi: Fetching ad sets for Meta campaign', { campaignId });
    const response = await apiClient.get<MetaAdSetList>(
      `/meta/campaigns/${encodeURIComponent(campaignId)}/ad-sets`,
    );
    return response.data;
  },

  async getAds(adSetId: string): Promise<MetaAdList> {
    debug.log('metaCampaignsApi: Fetching ads for Meta ad set', { adSetId });
    const response = await apiClient.get<MetaAdList>(
      `/meta/ad-sets/${encodeURIComponent(adSetId)}/ads`,
    );
    return response.data;
  },
};
